import { create } from 'zustand';
import { adminAuthService } from '../api/authService';
import type { AdminAuthState, AdminLoginRequest } from './types';

const TOKEN_KEY = 'admin_auth_token';
const TENANT_KEY = 'admin_active_tenant_id';
const SPOOF_TENANT_KEY = 'admin_spoofed_tenant_id';
const SPOOF_BEARER_KEY = 'admin_spoofed_bearer_token';
const SPOOF_SESSION_KEY = 'admin_spoofed_session_id';
const SPOOF_SESSION_LABEL_KEY = 'admin_spoofed_session_label';

export const useAdminAuthStore = create<AdminAuthState>((set, get) => ({
  token: localStorage.getItem(TOKEN_KEY),
  user: null,
  tenants: [],
  activeTenantId: localStorage.getItem(TENANT_KEY),
  spoofedTenantId: localStorage.getItem(SPOOF_TENANT_KEY),
  spoofedBearerToken: localStorage.getItem(SPOOF_BEARER_KEY),
  /* v8 ignore next -- IIFE runs once at module load; branch covered implicitly via setSpoofedSessionId */
  spoofedSessionId: (() => { const v = localStorage.getItem(SPOOF_SESSION_KEY); return v ? Number(v) : null; })(),
  spoofedSessionLabel: localStorage.getItem(SPOOF_SESSION_LABEL_KEY),
  isAuthenticated: !!localStorage.getItem(TOKEN_KEY),
  isLoading: false,
  error: null,

  login: async (credentials: AdminLoginRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await adminAuthService.login(credentials);
      const { token, user } = response.data;
      const tenants = user.tenants ?? [];
      localStorage.setItem(TOKEN_KEY, token);
      const persisted = localStorage.getItem(TENANT_KEY);
      const firstTenant = tenants[0];
      const activeTenantId =
        persisted ?? (firstTenant ? String(firstTenant.id) : null);
      if (activeTenantId) {
        localStorage.setItem(TENANT_KEY, activeTenantId);
      }
      set({
        token,
        user,
        tenants,
        activeTenantId,
        isAuthenticated: true,
        isLoading: false,
      });
      // Canonical user shape comes from /me - fire it here so later renders
      // see the full admin payload (roles, tenants with role, etc.) even if
      // the login response was minimal. Errors are deliberately swallowed so
      // a temporary /me failure does not unseat a successful login.
      try {
        await get().fetchUser();
      } catch {
        // ignore - user data from the login response is still usable
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Login failed';
      const apiMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? message;
      set({ error: apiMessage, isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    try {
      await adminAuthService.logout();
    } catch {
      // Ignore logout API errors - clear local state regardless
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TENANT_KEY);
      localStorage.removeItem(SPOOF_TENANT_KEY);
      localStorage.removeItem(SPOOF_BEARER_KEY);
      localStorage.removeItem(SPOOF_SESSION_KEY);
      localStorage.removeItem(SPOOF_SESSION_LABEL_KEY);
      set({
        token: null,
        user: null,
        tenants: [],
        activeTenantId: null,
        spoofedTenantId: null,
        spoofedBearerToken: null,
        spoofedSessionId: null,
        spoofedSessionLabel: null,
        isAuthenticated: false,
      });
    }
  },

  checkAuth: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    set({ isAuthenticated: !!token, token });
  },

  clearError: () => {
    set({ error: null });
  },

  setActiveTenant: (tenantId: string) => {
    localStorage.setItem(TENANT_KEY, tenantId);
    set({ activeTenantId: tenantId });
  },

  clearActiveTenant: () => {
    localStorage.removeItem(TENANT_KEY);
    set({ activeTenantId: null });
  },

  setSpoofedTenantId: (tenantId: string | null) => {
    if (tenantId) {
      localStorage.setItem(SPOOF_TENANT_KEY, tenantId);
    } else {
      localStorage.removeItem(SPOOF_TENANT_KEY);
    }
    set({ spoofedTenantId: tenantId });
  },

  setSpoofedBearerToken: (token: string | null) => {
    if (token) {
      localStorage.setItem(SPOOF_BEARER_KEY, token);
    } else {
      localStorage.removeItem(SPOOF_BEARER_KEY);
    }
    set({ spoofedBearerToken: token });
  },

  setSpoofedSessionId: (sessionId: number | null, label?: string | null) => {
    if (sessionId === null || sessionId === undefined) {
      localStorage.removeItem(SPOOF_SESSION_KEY);
      localStorage.removeItem(SPOOF_SESSION_LABEL_KEY);
    } else {
      localStorage.setItem(SPOOF_SESSION_KEY, String(sessionId));
      if (label) {
        localStorage.setItem(SPOOF_SESSION_LABEL_KEY, label);
      } else {
        localStorage.removeItem(SPOOF_SESSION_LABEL_KEY);
      }
    }
    set({ spoofedSessionId: sessionId, spoofedSessionLabel: label ?? null });
  },

  clearSpoofing: () => {
    localStorage.removeItem(SPOOF_TENANT_KEY);
    localStorage.removeItem(SPOOF_BEARER_KEY);
    localStorage.removeItem(SPOOF_SESSION_KEY);
    localStorage.removeItem(SPOOF_SESSION_LABEL_KEY);
    set({ spoofedTenantId: null, spoofedBearerToken: null, spoofedSessionId: null, spoofedSessionLabel: null });
  },

  fetchUser: async () => {
    try {
      const response = await adminAuthService.me();
      const user = response.data;
      const tenants = user.tenants ?? [];
      const persisted = localStorage.getItem(TENANT_KEY);
      const firstTenant = tenants[0];
      const activeTenantId =
        persisted ?? (firstTenant ? String(firstTenant.id) : null);
      if (activeTenantId) {
        localStorage.setItem(TENANT_KEY, activeTenantId);
      }
      set({ user, tenants, activeTenantId });
    } catch {
      // Ignore fetch errors - user will remain null
    }
  },
}));
